import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { withRouter } from 'react-router-dom';
import Classnames from 'classnames';
import { library } from '@fortawesome/fontawesome-svg-core';
import FactSheetDownload from './FactSheetDownload.js';
import Modal from 'react-responsive-modal';
import {
  deletePersonalStrategy,
  showPersonalStrategy,
  showAllStrategies,
  showStrategyEditor,
  updatePersonalStrategiesFromNew,
  updatePersonalStrategyName,
} from './../../../actions/data.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  formatDate,
  getKeyMetrics,
  getKeyMetricsSymbols,
} from './../../../common';
import './StrategyRow.css';
import StrategyAnalysis from './StrategyAnalysis.js';
import { KEY_METRICS } from './StrategyList.js';
import i18n from './../../../i18n';
import {
  faFileExcel,
  faBars,
  faTimes,
  faEdit,
  faFileLines,
  faRotateLeft,
  faRectangleList,
  faTrash,
  faExchangeAlt,
} from '@fortawesome/free-solid-svg-icons';
import { faFilePdf as faFilePdfRegular } from '@fortawesome/free-regular-svg-icons';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import StrategyEditor from 'wt-react/dist/components/StrategyEditor/StrategyEditor';

library.add(
  faFilePdfRegular,
  faFileExcel,
  faBars,
  faTimes,
  faEdit,
  faFileLines,
  faRotateLeft,
  faRectangleList,
  faTrash,
  faExchangeAlt
);

class StrategyRow extends PureComponent {
  static defaultProps = {
    saveInProgress: false,
    openMenu: false,
    striped: false,
    breakdownView: false,
  };

  constructor(props) {
    super(props);
    this.personalStratBase =
      (process.env.REACT_APP_PERSONAL_STRAT_URL || '').replace(/\/$/, '');
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.handleCloseDeleteModal = this.handleCloseDeleteModal.bind(this);
    this.handleDeleteConfirmation = this.handleDeleteConfirmation.bind(this);
    this.handleMenuClick = this.handleMenuClick.bind(this);
    this.handleShowEditor = this.handleShowEditor.bind(this);
    this.handleOptimizeInDpd = this.handleOptimizeInDpd.bind(this);
    this.handleBreadcrumbClick = this.handleBreadcrumbClick.bind(this);

    this.state = {
      startDeletion: false,
      openMenu: this.props.openMenu,
      saveInProgress: props.saveInProgress,
      isEditingName: false,
      newName: this.props.personalStrategy?.name || null,
    };

    this.handleNameClick = this.handleNameClick.bind(this);
    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleNameBlur = this.handleNameBlur.bind(this);
  }

  // Build icon URL from the personal strategies host
  getIconSrc = (file) => {
    const host = (process.env.REACT_APP_PERSONAL_STRAT_URL || '').replace(
      /\/$/,
      ''
    );
    return `${host}/iconsBlue/${file}`;
  };

  // ---------- data helpers ----------
  getRawAssetClassTotals = (ps) => {
    if (!ps || typeof ps !== 'object') return {};
    const raw = ps.assetClassTotals;
    return raw && typeof raw === 'object' ? raw : {};
  };

  toPercentBreakdown = (raw) => {
    // read each bucket; if missing, assume 0
    const equityRaw = raw.Equity ?? 0;
    const fixedRaw = raw['Fixed Income'] ?? raw.FixedIncome ?? 0;
    const altsRaw = raw.Alternatives ?? raw.Alternative ?? 0;

    // convert decimals (0–1) to whole-number % and round
    const toPct = (x) => Math.round(Number(x || 0) * 100);

    const equityPct = toPct(equityRaw);
    const fixedPct = toPct(fixedRaw);
    const altsPct = toPct(altsRaw);
    return { equity: equityPct, fixed: fixedPct, alts: altsPct };
  };

  buildAssetBreakdownString = (ps) => {
    const raw = this.getRawAssetClassTotals(ps);
    const { equity, fixed, alts } = this.toPercentBreakdown(raw);
    // Stable sort parts by value desc; ties keep original order
    const parts = [
      { label: 'EQ', val: equity, ord: 0 },
      { label: 'Fxd Inc', val: fixed, ord: 1 },
      { label: 'Alts', val: alts, ord: 2 },
    ]
      .filter((p) => p.val > 0)
      .sort((a, b) => b.val - a.val || a.ord - b.ord);

    if (!parts.length) return '-';
    return parts.map((p) => `${p.label} ${p.val}%`).join(' | ');
  };

  componentDidUpdate(prevProps) {
    if (this.props.openMenu !== prevProps.openMenu) {
      this.props.setMenuIsOpen(this.props.openMenu);
    }
  }

  // ---------- Type detection & permissions ----------
  hasPortHubType = (ps, target) => {
    if (!ps) return false;
    const raw = ps.portHubType;
    const match = (v) => String(v).trim().toUpperCase() === target;
    return Array.isArray(raw) ? raw.some(match) : match(raw);
  };

  getTypeFlags = (ps) => {
    const typeUpper = String(ps?.type || '')
      .trim()
      .toUpperCase();
    const isUserFavorite = this.hasPortHubType(ps, 'USER-FAVORITE');
    const isManagedCio = this.hasPortHubType(ps, 'MANAGED-CIO');
    const isSubscribed =
      ps?.subscribed === true ||
      this.hasPortHubType(ps, 'SUBSCRIBED') ||
      this.hasPortHubType(ps, 'SUBSCRIPTION');
    // Models on Demand should not be editable; they map to portHubType "user-custom-model"
    const isCustomOnDemand = this.hasPortHubType(ps, 'USER-CUSTOM-MODEL');
    const isCOD =
      typeUpper === 'COD' ||
      this.hasPortHubType(ps, 'CUSTOM ON DEMAND') ||
      this.hasPortHubType(ps, 'CUSTOM-ON-DEMAND');
    return {
      isUserFavorite,
      isManagedCio,
      isSubscribed,
      isCOD,
      isCustomOnDemand,
    };
  };

  getPermissions = (ps) => {
    const {
      isUserFavorite,
      isManagedCio,
      isSubscribed,
      isCOD,
      isCustomOnDemand,
    } = this.getTypeFlags(ps);
    const hideEdit =
      isSubscribed ||
      isUserFavorite ||
      isManagedCio ||
      isCOD ||
      isCustomOnDemand;
    const hideDelete = isUserFavorite || isManagedCio;
    const hideOptimize = isSubscribed || isManagedCio || isCOD;
    return { hideEdit, hideDelete, hideOptimize };
  };
  // --------------------------------------------------

  handleDeleteClick() {
    this.setState({ startDeletion: true, openMenu: false });
  }
  handleCloseDeleteModal() {
    this.setState({ startDeletion: false });
  }
  handleDeleteConfirmation(idToDelete) {
    this.setState({ startDeletion: false });
    this.props.deletePersonalStrategy(idToDelete);
  }
  handleMenuClick(idToShow) {
    !this.props.openMenu
      ? this.props.showPersonalStrategy(idToShow)
      : this.props.showAllStrategies();
  }
  handleShowEditor(strategyToEdit) {
    this.props.showStrategyEditor(strategyToEdit);
  }

  // REQUIRED: used by constructor bind & Optimize button
  handleOptimizeInDpd(strategyId) {
    this.props.showAllStrategies();
    window.location = '#dpd&portfolioid=' + strategyId;
  }

  // Navigate to /portfolio-solutions/model-portfolios in a new tab
  handleGoToModelPerformance = () => {
    window.location.assign('/portfolio-solutions/model-portfolios');
  };

  handleViewInFundChar = () => {
    this.props.showAllStrategies();
    window.location = '#fundchar&landingTab=3';
  };

  handleRefreshData(strategyToOptimize) {
    this.props.showAllStrategies();
    window.location = '#dpd&portfolioid=' + strategyToOptimize.id;
  }

  generateExcel = async () => {
    const { personalStrategy, allStrategies } = this.props;
    const wb = new ExcelJS.Workbook();

    const allocationsSheet = wb.addWorksheet('Allocations');
    allocationsSheet.addRow([
      'Trade Date',
      'Portfolio Name',
      'Asset Class',
      'Ticker',
      'Weight',
      'Turnover',
      'Name',
    ]);

    if (
      personalStrategy &&
      personalStrategy?.customUserModelTradeNotifications
    ) {
      const tradeNotifications =
        personalStrategy?.customUserModelTradeNotifications;
      const portfolioName = personalStrategy?.name;

      if (tradeNotifications) {
        tradeNotifications.forEach((notification) => {
          const dateObject = notification.date
            ? new Date(notification.date)
            : new Date();
          const date = dateObject.toLocaleDateString();
          const turnover = Number(notification.turnOver.toFixed(2));
          allocationsSheet.addRow([
            date,
            portfolioName,
            notification.assetClass,
            notification.ticker,
            notification.weight,
            turnover,
            notification.name,
          ]);
        });
      }

      allocationsSheet.getColumn(1).width = 12;
      allocationsSheet.getColumn(2).width = 60;
      allocationsSheet.getColumn(3).width = 15;
      allocationsSheet.getColumn(7).width = 60;
      allocationsSheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
      });
    }

    // Disclosures
    const disclosuresSheet = wb.addWorksheet('Disclosures');
    disclosuresSheet.columns = [{ key: 'disclosures', width: 100 }];
    [
      [i18n.t('excel-disclosure-text-1')],
      [i18n.t('excel-disclosure-text-2')],
      [i18n.t('excel-disclosure-text-3')],
      [i18n.t('excel-disclosure-text-4')],
      [i18n.t('excel-disclosure-text-5')],
      [i18n.t('excel-disclosure-text-6')],
      [i18n.t('excel-disclosure-text-7')],
    ].forEach((row, idx) => {
      const r = disclosuresSheet.addRow(row);
      if (idx === 3)
        r.eachCell((c) => {
          c.font = { bold: true };
        });
    });
    disclosuresSheet.getColumn(1).eachCell((c) => {
      c.alignment = { wrapText: true };
    });

    // All Custom Models sheet (last rebalance snapshot)
    const allSheet = wb.addWorksheet('All Custom Models');
    allSheet.addRow([
      'Trade Date',
      'Portfolio Name',
      'Asset Class',
      'Ticker',
      'Weight',
      'Turnover',
      'Name',
    ]);
    allStrategies.forEach((strategy) => {
      if (strategy.customUserModel && strategy.customUserModelRebalances) {
        const lastItem = strategy.customUserModelRebalances.at(-1);
        const { assetClasses, tickers, weights, names } = lastItem;
        const date = new Date(lastItem.date).toLocaleDateString();
        const portfolioName = strategy.name;
        const turnover = 55;

        for (let i = 0; i < assetClasses.length; i++) {
          allSheet.addRow([
            date,
            portfolioName,
            assetClasses[i],
            tickers[i],
            weights[i],
            turnover,
            names[i],
          ]);
        }
        allSheet.getColumn(1).width = 12;
        allSheet.getColumn(2).width = 60;
        allSheet.getColumn(3).width = 15;
        allSheet.getColumn(7).width = 60;
        allSheet.getRow(1).eachCell((c) => {
          c.font = { bold: true };
        });
      }
    });

    const buffer = await wb.xlsx.writeBuffer();
    const name = personalStrategy?.name;
    const filename = name ? `${name}.xlsx` : 'TradeNotification.xlsx';
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, filename);
  };

  setSavingStrategyInProgress = (value, isSaving) => {
    this.setState({
      saveInProgress: isSaving,
      newName: null,
      isEditingName: false,
    });
    if (!isSaving && value) this.props.updatePersonalStrategiesFromNew(value);
  };

  // Callback from StrategyEditor when an existing portfolio is saved
  handleStrategyUpdate = (value, isSaving) => {
    this.setState({
      saveInProgress: isSaving,
      newName: value?.name || this.state.newName,
      isEditingName: false,
    });

    if (!isSaving && value) {
      this.props.updatePersonalStrategiesFromNew(value);
    }
  };

  // ========= Edit/Delete (unchanged behavior) =========
  renderEditAndDeleteButtons = (
    saveInProgress,
    strategyTypeIsCustom,
    personalStrategy,
    openMenu,
    params
  ) => {
    const { hideEdit, hideDelete } = this.getPermissions(personalStrategy);

    const editButton = !hideEdit ? (
      <div data-tip data-for={'edit-portfolio-tooltip'}>
        <StrategyEditor
          type="update"
          strategyToEdit={personalStrategy}
          closeEditor={this.props.showAllStrategies}
          checkTickersUrl={process.env.REACT_APP_DPD_URL || ''}
          dataspanApiUrl={process.env.REACT_APP_API_URL || ''}
          benchmarks={[]}
          token={params?.token}
          clientEmail={params?.clientEmail}
          typeHeadPlaceholder="Search By Ticker or Name"
          savedStrategyCallBack={this.handleStrategyUpdate}
          disableUpdate={false}
        />
      </div>
    ) : openMenu ? (
      <div className="menu-button" style={{ cursor: 'default' }}>
        <FontAwesomeIcon icon={faEdit} size="lg" color="transparent" />
      </div>
    ) : null;

    const deleteButton = !hideDelete ? (
      <div
        className="menu-button"
        data-tip
        data-for={'delete-x-tooltip'}
        onClick={this.handleDeleteClick}
      >
        <FontAwesomeIcon icon={faTrash} size="lg" />
      </div>
    ) : null;

    return (
      <>
        {!saveInProgress ? (
          <div className="edit-delete-buttons">
            {editButton}
            {deleteButton}
            <ReactTooltip
              key={'delete-x-tooltip'}
              id={'delete-x-tooltip'}
              type="light"
              className="tooltip"
              effect="float"
            >
              {i18n.t('delete-x-tooltip')}
            </ReactTooltip>
            <ReactTooltip
              key={'edit-portfolio-tooltip'}
              id={'edit-portfolio-tooltip'}
              type="light"
              className="tooltip"
              effect="float"
            >
              {i18n.t('edit-portfolio-tooltip')}
            </ReactTooltip>
          </div>
        ) : (
          <div className="waiting"></div>
        )}
      </>
    );
  };

  // ========= NEW: Plain-English action area =========
  renderActionButtons = (ps, openMenu) => {
    const strategyTypeIsCustom = !!ps?.customUserModel;
    const { isManagedCio, isSubscribed, isUserFavorite, isCustomOnDemand } =
      this.getTypeFlags(ps);
    const { hideOptimize } = this.getPermissions(ps);
    const isUserModel = this.hasPortHubType(ps, 'USER-MODEL');

    // CASE 1: Subscribed or CIO-managed → NO Fund Compare, NO Optimize
    if (isSubscribed || isManagedCio) {
      return (
        <>
          <button
            className="icon-only-btn"
            onClick={this.handleViewInFundChar}
            data-tip
            data-for={'fund-compare-tooltip-' + ps.id}
            aria-label={i18n.t('fund-comparison')}
          >
            <img
              className="icon-btn icon-portfolio-hub icon-fund-compare"
              src={this.getIconSrc('PieRightSlice.svg')}
              alt="Fund Compare"
              title="Compare Models"
              width={32}
              height={32}
            />
          </button>
          <ReactTooltip
            key={'fund-compare-tooltip-' + ps.id}
            id={'fund-compare-tooltip-' + ps.id}
            type="light"
            className="tooltip"
            effect="float"
          >
            {i18n.t('fund-compare-tooltip')}
          </ReactTooltip>

          <button
            className="icon-only-btn"
            onClick={this.handleGoToModelPerformance}
            aria-label="Model Performance"
            data-tip
            data-for={'model-performance-tooltip-' + ps.id}
          >
            <img
              className="icon-btn icon-portfolio-hub icon-model-performance"
              src={this.getIconSrc('PortfolioCombo.svg')}
              alt="Model Performance"
              title="Click to visit Model Performance"
              width={32}
              height={32}
            />
          </button>
          <ReactTooltip
            key={'model-performance-tooltip-' + ps.id}
            id={'model-performance-tooltip-' + ps.id}
            type="light"
            className="tooltip"
            effect="float"
          >
            Click to visit Model Performance
          </ReactTooltip>
          {strategyTypeIsCustom && (
            <button
              onClick={this.generateExcel}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                outline: 'inherit',
              }}
            >
              <FontAwesomeIcon icon={faFileExcel} />{' '}
              {i18n.t('trade-notification')}
            </button>
          )}
          <FactSheetDownload
            type="factsheet"
            personalStrategyID={ps.id}
            openMenu={openMenu}
            iconOnly={true}
            tooltip={i18n.t('pdf-download-tooltip')}
          />
          {(strategyTypeIsCustom || isCustomOnDemand) && (
            <FactSheetDownload
              type="commentary"
              personalStrategyID={ps.id}
              openMenu={openMenu}
              iconOnly={true}
              personalStrategyModelType={
                ps.customUserModelUnderlyingModelFamily
              }
              tooltip={i18n.t('commentary-download-tooltip')}
            />
          )}

          <button
            className="icon-only-btn"
            onClick={this.handleMenuClick.bind(this, ps.id)}
            data-tip
            data-for={'back-to-list-tooltip'}
            aria-label={i18n.t('back-to-list')}
          >
            <FontAwesomeIcon
              className="icon-btn icon-return-to-list"
              icon={faRotateLeft}
              title={i18n.t('back-to-list')}
            />
          </button>
          <ReactTooltip
            key={'back-to-list-tooltip'}
            id={'back-to-list-tooltip'}
            type="light"
            className="tooltip"
            effect="float"
          >
            {i18n.t('back-to-list-tooltip')}
          </ReactTooltip>
        </>
      );
    }

    // CASE 2: User-Model AND Optimize allowed → show Optimize
    if (isUserModel && !hideOptimize) {
      return (
        <>
          <button
            className="icon-only-btn"
            onClick={this.handleOptimizeInDpd.bind(this, ps.id)}
            data-tip
            data-for={'optimize-in-dpd-tooltip'}
            aria-label={i18n.t('optimize-in-dpd')}
          >
            <img
              className="icon-btn icon-portfolio-hub icon-optimize-portfolio"
              src={this.getIconSrc('ChartCombo.svg')}
              alt={i18n.t('optimize-in-dpd')}
              title={i18n.t('optimize-in-dpd')}
              width={32}
              height={32}
            />
          </button>
          <ReactTooltip
            key={'optimize-in-dpd-tooltip'}
            id={'optimize-in-dpd-tooltip'}
            type="light"
            className="tooltip"
            effect="float"
          >
            {i18n.t('optimize-in-dpd-tooltip')}
          </ReactTooltip>

          <FactSheetDownload
            type="factsheet"
            personalStrategyID={ps.id}
            openMenu={openMenu}
            iconOnly={true}
            tooltip={i18n.t('pdf-download-tooltip')}
          />
          {(strategyTypeIsCustom || isCustomOnDemand) && (
            <FactSheetDownload
              type="commentary"
              personalStrategyID={ps.id}
              openMenu={openMenu}
              iconOnly={true}
              personalStrategyModelType={
                ps.customUserModelUnderlyingModelFamily
              }
              tooltip={i18n.t('commentary-download-tooltip')}
            />
          )}
          {strategyTypeIsCustom && (
            <button
              onClick={this.generateExcel}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                outline: 'inherit',
              }}
            >
              <FontAwesomeIcon icon={faFileExcel} />{' '}
              {i18n.t('trade-notification')}
            </button>
          )}

          <button
            className="icon-only-btn"
            onClick={this.handleMenuClick.bind(this, ps.id)}
            data-tip
            data-for={'back-to-list-tooltip'}
            aria-label={i18n.t('back-to-list')}
          >
            <FontAwesomeIcon
              className="icon-btn icon-return-to-list icon-return-to-list-large"
              icon={faRotateLeft}
              title={i18n.t('back-to-list')}
            />
          </button>
          <ReactTooltip
            key={'back-to-list-tooltip'}
            id={'back-to-list-tooltip'}
            type="light"
            className="tooltip"
            effect="float"
          >
            {i18n.t('back-to-list-tooltip')}
          </ReactTooltip>
        </>
      );
    }

    // CASE 3: Default → allow Fund Compare (except Favorites), no Optimize
    const showFundCompare = true;
    const returnIconClass = Classnames('icon-btn icon-return-to-list', {
      'icon-return-to-list-large': isCustomOnDemand,
    });
    const fundCompareIcon = (
      <img
        className="icon-btn icon-portfolio-hub icon-fund-compare"
        src={this.getIconSrc('PieRightSlice.svg')}
        alt="Fund Compare"
        title="Compare Models"
        width={32}
        height={32}
      />
    );
    const modelPerfIcon = (
      <img
        className="icon-btn icon-portfolio-hub icon-model-performance icon-model-performance-large"
        src={this.getIconSrc('PortfolioCombo.svg')}
        alt="Model Performance"
        title="Model Performance"
        width={32}
        height={32}
      />
    );
    return (
      <>
        {showFundCompare && (
          <>
            <button
              className="icon-only-btn"
              onClick={this.handleViewInFundChar}
              data-tip
              data-for={'fund-compare-tooltip-' + ps.id}
              aria-label={i18n.t('fund-comparison')}
            >
              {fundCompareIcon}
            </button>
            <ReactTooltip
              key={'fund-compare-tooltip-' + ps.id}
              id={'fund-compare-tooltip-' + ps.id}
              type="light"
              className="tooltip"
              effect="float"
            >
              {i18n.t('fund-compare-tooltip')}
        </ReactTooltip>
      </>
    )}

        <button
          className="icon-only-btn"
          onClick={this.handleGoToModelPerformance}
          aria-label="Model Performance"
          data-tip
          data-for={'model-performance-tooltip-' + ps.id}
          title="Click to visit Model Performance"
        >
          {modelPerfIcon}
        </button>
        <ReactTooltip
          key={'model-performance-tooltip-' + ps.id}
          id={'model-performance-tooltip-' + ps.id}
          type="light"
          className="tooltip"
          effect="float"
        >
          Click to visit Model Performance
        </ReactTooltip>

        <FactSheetDownload
          type="factsheet"
          personalStrategyID={ps.id}
          openMenu={openMenu}
          iconOnly={isUserFavorite || isCustomOnDemand}
          tooltip={i18n.t('pdf-download-tooltip')}
        />
        {(strategyTypeIsCustom || isCustomOnDemand) && (
          <FactSheetDownload
            type="commentary"
            personalStrategyID={ps.id}
            openMenu={openMenu}
            iconOnly={isUserFavorite || isCustomOnDemand}
            personalStrategyModelType={ps.customUserModelUnderlyingModelFamily}
            tooltip={i18n.t('commentary-download-tooltip')}
          />
        )}
        {strategyTypeIsCustom && (
          <button
            className="icon-only-btn"
            onClick={this.generateExcel}
            data-tip
            data-for={'trade-notification-tooltip-' + ps.id}
            aria-label={i18n.t('trade-notification')}
          >
            <FontAwesomeIcon
              className="icon-btn icon-trade-notify"
              icon={faExchangeAlt}
              title={i18n.t('trade-notification')}
            />
          </button>
        )}

        <button
          className="icon-only-btn"
          onClick={this.handleMenuClick.bind(this, ps.id)}
          data-tip
          data-for={'back-to-list-tooltip'}
          aria-label={i18n.t('back-to-list')}
        >
          <FontAwesomeIcon
            className={returnIconClass}
            icon={faRotateLeft}
            title={i18n.t('back-to-list')}
          />
        </button>
        <ReactTooltip
          key={'back-to-list-tooltip'}
          id={'back-to-list-tooltip'}
          type="light"
          className="tooltip"
          effect="float"
        >
          {i18n.t('back-to-list-tooltip')}
        </ReactTooltip>
      </>
    );
  };
  // ================================================

  handleBreadcrumbClick(breadcrumb) {
    const nav = document.getElementById(breadcrumb);
    if (nav) nav.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  handleNameClick() {
    this.setState({ isEditingName: true });
  }
  handleNameChange(e) {
    this.setState({ newName: e.target.value });
  }
  async handleNameBlur() {
    this.setState({ isEditingName: false });
    const { newName } = this.state;
    const { personalStrategy, updatePersonalStrategyName } = this.props;
    try {
      await updatePersonalStrategyName(personalStrategy?.id, newName);
    } catch (error) {
      console.error('Error updating name:', error);
    }
  }

  render() {
    const { personalStrategy, striped, openMenu, params } = this.props;
    const { saveInProgress } = this.state;
    const strategyTypeIsCustom = personalStrategy?.customUserModel;
    const metricsValues = personalStrategy?.statistics
      ? getKeyMetrics(personalStrategy.statistics)
      : null;
    const metricsSymbols = personalStrategy ? getKeyMetricsSymbols() : null;
    const { isManagedCio, isUserFavorite, isCustomOnDemand, isSubscribed } =
      this.getTypeFlags(personalStrategy);
    const useBreakdownLayout =
      !!this.props.breakdownView || isManagedCio || isUserFavorite;
    const isUserModel = this.hasPortHubType(personalStrategy, 'USER-MODEL');
    //nst { hideOptimize } = this.getPermissions(personalStrategy);
    const { isEditingName, newName } = this.state;

    // Only allow optimize when portHubType contains "user-model"
    //nst isUserModel = this.hasPortHubType(personalStrategy, 'USER-MODEL');

    return (
      <div>
        <div
          className={Classnames(
            'strategyrow',
            { striped: striped && !openMenu },
            { opened: openMenu },
            { 'breakdown-view': !!this.props.breakdownView }
          )}
        >
          <div
            className="menu"
            onClick={this.handleMenuClick.bind(this, personalStrategy?.id)}
          >
            <FontAwesomeIcon
              icon={faBars}
              data-tip
              data-for={'click-to-view-details-tooltip' + personalStrategy?.id}
              color={strategyTypeIsCustom ? '#0047bb' : null}
            />
          </div>
          <ReactTooltip
            key={'click-to-view-details-tooltip' + personalStrategy?.id}
            id={'click-to-view-details-tooltip' + personalStrategy?.id}
            type="light"
            className="tooltip"
            effect="float"
          >
            {openMenu
              ? i18n.t('click-to-go-back-to-list-tooltip')
              : i18n.t('click-to-view-details-tooltip')}
          </ReactTooltip>

          <>
            {openMenu ? (
              isEditingName ? (
                <input
                  type="text"
                  value={newName}
                  onChange={this.handleNameChange}
                  onBlur={this.handleNameBlur}
                  autoFocus
                  className="input-style"
                />
              ) : (
                <div className="name" onClick={this.handleNameClick}>
                  {newName}
                </div>
              )
            ) : (
              <div
                className="name"
                onClick={this.handleMenuClick.bind(this, personalStrategy?.id)}
              >
                {newName ? newName : personalStrategy?.name}
              </div>
            )}
          </>

          {!openMenu ? (
            <div
              className="inception-date"
              onClick={this.handleMenuClick.bind(this, personalStrategy?.id)}
            >
              {formatDate(
                strategyTypeIsCustom
                  ? personalStrategy?.createDate ||
                      personalStrategy?.inceptionDate ||
                      personalStrategy?.commonPeriodStart
                  : personalStrategy?.inceptionDate ||
                      personalStrategy?.commonPeriodStart
              )}
            </div>
          ) : null}

          {!openMenu ? (
            <div
              className={Classnames('statistics', {
                'no-sharpe': useBreakdownLayout,
                'asset-left': useBreakdownLayout,
                'custom-model': isCustomOnDemand,
              })}
            >
          {(useBreakdownLayout
            ? ['performance', 'income', 'risk', 'fees']
            : KEY_METRICS
          ).map((metric) => {
            const value = metricsValues ? metricsValues[metric] : null;
            const isUserBuilt = this.hasPortHubType(
              personalStrategy,
              'USER-MODEL'
            );

            // Tooltip logic for performance column (row/detail)
            let metricTooltip = '';
            if (metric === 'performance') {
              if (isUserBuilt) {
                const start = personalStrategy?.commonPeriodStart;
                const end = personalStrategy?.commonPeriodEnd;
                const startLabel = start ? formatDate(start) : '-';
                const endLabel = end ? formatDate(end) : '-';
                metricTooltip = `Common period: ${startLabel} - ${endLabel}`;
              } else {
                const inception = personalStrategy?.inceptionDate;
                const inceptionLabel = inception ? formatDate(inception) : '-';
                metricTooltip = `Performance Since Inception Date: ${inceptionLabel}`;
              }
            }

            const missingCustomMsg =
              !value && strategyTypeIsCustom && value === '-'
                ? 'This data is available after 3 months from inception of this custom model.'
                : '';
            const dataTipText = metricTooltip || missingCustomMsg;

            return (
              <div key={'stat-' + metric} className={'stat-' + metric}>
                <span
                  className="center-dash"
                  data-tip={dataTipText}
                  data-for={metric + '-tooltip-' + personalStrategy?.id}
                >
                  {metric === 'risk' && (isManagedCio || isUserFavorite)
                    ? this.buildAssetBreakdownString(personalStrategy)
                    : value
                        ? value
                        : '-'}

                      {(metric !== 'risk' ||
                        (!isManagedCio && !isUserFavorite)) &&
                        value &&
                        value !== '-' && (
                      <span className="symbol">
                        {metric === 'fees'
                          ? ` ${metricsSymbols[metric]}`
                          : metricsSymbols[metric]}
                      </span>
                    )}
                </span>
                <ReactTooltip
                  id={metric + '-tooltip-' + personalStrategy?.id}
                  className="tooltip"
                  type="light"
                  effect="float"
                >
                  {dataTipText}
                </ReactTooltip>
              </div>
            );
          })}
        </div>
          ) : openMenu ? (
            <div className="statistics open-menu">
              <div
                className={Classnames('select_button_action', {
                  'tight-icons':
                    isManagedCio ||
                    isSubscribed ||
                    isUserModel ||
                    isCustomOnDemand,
                })}
              >
                {this.renderActionButtons(personalStrategy, openMenu)}
              </div>

              {this.renderEditAndDeleteButtons(
                saveInProgress,
                strategyTypeIsCustom,
                personalStrategy,
                openMenu,
                params
              )}
            </div>
          ) : (
            <div className="statistics" />
          )}

          {!openMenu &&
            this.renderEditAndDeleteButtons(
              saveInProgress,
              strategyTypeIsCustom,
              personalStrategy,
              openMenu,
              params
            )}

          {this.state.startDeletion ? (
            <Modal
              open={this.state.startDeletion === true}
              onClose={this.handleCloseDeleteModal}
              showCloseIcon={false}
              classNames={{
                overlay: 'modal-overlay-port-hub',
                modal: 'delete-modal',
                closeIcon: 'custom-closeIcon',
              }}
              center
            >
              <div>
                <br />
                {i18n.t('confirm-delete')}
                <strong>{personalStrategy?.name}</strong>?<br />
                <br />
                <div className="align-right">
                  <button
                    onClick={this.handleDeleteConfirmation.bind(
                      this,
                      personalStrategy?.id
                    )}
                  >
                    {i18n.t('delete')}
                  </button>
                  <button onClick={this.handleCloseDeleteModal}>
                    {i18n.t('cancel')}
                  </button>
                </div>
              </div>
            </Modal>
          ) : null}
        </div>

        {this.props.openMenu ? (
          <StrategyAnalysis
            personalStrategy={personalStrategy}
            openHoldingsEditor={this.handleShowEditor.bind(
              this,
              personalStrategy
            )}
          />
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  allStrategies: state.data.personalStrategies,
  params: state.params,
  benchmarks: state.data.benchmarks,
});

export default withRouter(
  connect(mapStateToProps, {
    deletePersonalStrategy,
    showPersonalStrategy,
    showAllStrategies,
    showStrategyEditor,
    updatePersonalStrategiesFromNew,
    updatePersonalStrategyName,
  })(StrategyRow)
);
